import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import { useTenant } from '../../lib/api/hooks.js';
import { useUpdateTenant } from '../../lib/api/hooks-billing.js';
import { formatMoney } from '../../lib/format.js';
import { Icon } from '../../components/ui/Icon.js';
import { ErrorBanner, Empty, TableSkeleton, Badge } from '../../components/ui/Primitives.js';

/**
 * Spend against its limits (0132, owner 26 September 2026: "people can then track their spend easier
 * and still use budgets and limits even on their own trunks"). Spend is what we charged, or over the
 * account's own trunk what its carrier charges on its uploaded sheet. At a limit, outside calls stop
 * until the period turns; internal and emergency calls never do.
 */

interface SeatSpend {
  extension_id: string; number: string; name: string; budget: string; period: 'daily' | 'weekly' | 'monthly';
  period_start: string; resets_at: string; spent: string; over: boolean; currency: string;
}
interface AccountSpend { cap: string | null; month_start: string; resets_at: string; spent: string; over: boolean; currency: string }

const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
const PERIOD = { daily: 'today', weekly: 'this week', monthly: 'this month' } as const;

export function useAccountSpend(tenantId: string | null) {
  const can = useSession((s) => s.can);
  return useQuery({
    queryKey: ['spend', tenantId],
    queryFn: () => api.get<{ account: AccountSpend; extensions: SeatSpend[] }>(`/v1/tenants/${tenantId}/spend`),
    enabled: !!tenantId && can('cdr:read'),
    refetchInterval: 60_000,
  });
}

export function useExtensionSpend(tenantId: string, extensionId: string | null) {
  const can = useSession((s) => s.can);
  return useQuery({
    queryKey: ['spend', tenantId, extensionId],
    queryFn: () => api.get<{ extension: SeatSpend | null; account_blocked: boolean }>(`/v1/tenants/${tenantId}/extensions/${extensionId}/spend`),
    enabled: !!extensionId && can('extension:read'),
    refetchInterval: 60_000,
  });
}

/** Only while a limit has stopped calls: banners are for problems. */
export function SpendBanner({ tenantId, extensionId }: { tenantId: string; extensionId?: string | undefined }) {
  const seat = useExtensionSpend(tenantId, extensionId ?? null);
  const account = useAccountSpend(extensionId ? null : tenantId);
  const s = seat.data?.extension;
  const text = extensionId
    ? seat.data?.account_blocked ? 'The account has reached its monthly spend cap: outside calls are stopped until next month.'
      : s?.over ? `This extension has reached its ${s.period} budget of ${formatMoney(s.budget, s.currency)}: outside calls are stopped until ${day(s.resets_at)}.`
      : null
    : account.data?.account.over ? `The account has reached its monthly spend cap of ${formatMoney(account.data.account.cap, account.data.account.currency)}: outside calls are stopped until ${day(account.data.account.resets_at)}.`
      : (account.data?.extensions.filter((e) => e.over).length ?? 0) > 0
        ? `${account.data!.extensions.filter((e) => e.over).map((e) => e.name).join(', ')} ${account.data!.extensions.filter((e) => e.over).length === 1 ? 'has' : 'have'} reached ${account.data!.extensions.filter((e) => e.over).length === 1 ? 'its' : 'their'} spend budget: outside calls from ${account.data!.extensions.filter((e) => e.over).length === 1 ? 'it are' : 'them are'} stopped.`
        : null;
  if (!text) return null;
  return (
    <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }} role="status">
      <Icon name="warning" size={14} />
      <span>{text} Internal and emergency calls still work. Raise the limit to allow calls now.</span>
    </div>
  );
}

/** A bar of spent against limit. */
function Bar({ spent, limit }: { spent: string; limit: string }) {
  const pct = Math.min(100, Math.round((Number(spent) / Math.max(Number(limit), 0.000001)) * 100));
  const tone = pct >= 100 ? 'var(--arc-danger)' : pct >= 80 ? 'var(--arc-warn)' : 'var(--arc-ok)';
  return (
    <div className="arc-row" style={{ gap: 8, minWidth: 160 }}>
      <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--arc-surface-3)', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: tone }} />
      </div>
      <span className="arc-num arc-subtle" style={{ fontSize: 12, width: 36, textAlign: 'right' }}>{pct}%</span>
    </div>
  );
}

/** One seat's budget, on its own page beside the field that sets it. */
export function ExtensionSpendLine({ tenantId, extensionId }: { tenantId: string; extensionId: string }) {
  const q = useExtensionSpend(tenantId, extensionId);
  const s = q.data?.extension;
  if (!s) return null;
  return (
    <div className="arc-stack" style={{ gap: 4, marginTop: -4, marginBottom: 12 }}>
      <span className="arc-muted" style={{ fontSize: 12.5 }}>
        {formatMoney(s.spent, s.currency)} of {formatMoney(s.budget, s.currency)} spent {PERIOD[s.period]}; resets {day(s.resets_at)}.
      </span>
      <Bar spent={s.spent} limit={s.budget} />
    </div>
  );
}

export function SpendLimitsPage({ tenantId, onOpenExtension }: { tenantId: string; onOpenExtension?: (id: string) => void }) {
  const can = useSession((s) => s.can);
  const tenant = useTenant(tenantId);
  const spend = useAccountSpend(tenantId);
  const update = useUpdateTenant();
  const [cap, setCap] = useState<string | null>(null);
  const a = spend.data?.account;
  const current = tenant.data?.spend_cap_monthly ?? null;
  const value = cap ?? (current ? String(Number(current)) : '');
  const valid = value === '' || /^\d+(\.\d{1,2})?$/.test(value.trim());
  const dirty = valid && (value.trim() === '' ? current !== null : Number(value) !== Number(current ?? NaN));
  const mayWrite = can('tenant:write');

  return (
    <div className="arc-stack" style={{ gap: 14 }}>
      <SpendBanner tenantId={tenantId} />
      {spend.error && <ErrorBanner error={spend.error} />}

      <div className="arc-card">
        <div className="arc-card-head">
          <h2 className="arc-h2">This month</h2>
          <span className="arc-subtle">what calls cost, on ArcNet trunks and your own</span>
        </div>
        <div className="arc-card-body">
          {!a ? <span className="arc-subtle">…</span> : (
            <div className="arc-row" style={{ gap: 28, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div className="arc-stack" style={{ gap: 2 }}>
                <span className="arc-label">Spent since {day(a.month_start)}</span>
                <span style={{ fontSize: 22, fontWeight: 600 }} className="arc-num">{formatMoney(a.spent, a.currency)}</span>
                {a.cap && <Bar spent={a.spent} limit={a.cap} />}
              </div>
              <div className="arc-field" style={{ margin: 0, maxWidth: 240 }}>
                <label className="arc-label" htmlFor="cap">Monthly spend cap</label>
                <input id="cap" className="arc-input arc-num" inputMode="decimal" placeholder="No cap" value={value}
                  disabled={!mayWrite} aria-invalid={!valid || undefined}
                  onChange={(e) => setCap(e.target.value.replace(/[^\d.]/g, ''))} />
                <div className={valid ? 'arc-hint' : 'arc-error'}>
                  {valid ? 'Outside calls stop for the rest of the month once reached. Blank for no cap.' : 'An amount, e.g. 5000.'}
                </div>
                {mayWrite && (
                  <div className="arc-row" style={{ gap: 8, marginTop: 8 }}>
                    <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={!dirty || update.isPending}
                      onClick={() => update.mutate(
                        { id: tenantId, spend_cap_monthly: value.trim() === '' ? null : Number(value).toFixed(2) },
                        { onSuccess: () => { setCap(null); void spend.refetch(); } })}>
                      {update.isPending ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                )}
                {update.error && <ErrorBanner error={update.error} />}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="arc-card">
        <div className="arc-card-head">
          <h2 className="arc-h2">Extension budgets</h2>
          <span className="arc-subtle">set on each extension’s Details</span>
        </div>
        {spend.data === undefined ? <div className="arc-table-wrap"><table className="arc-table"><TableSkeleton cols={4} /></table></div>
          : spend.data.extensions.length === 0 ? (
            <div className="arc-card-body"><Empty title="No extension has a budget" hint="Give one a daily, weekly or monthly budget on its Details to stop its outside calls at that amount." /></div>
          ) : (
            <div className="arc-table-wrap">
              <table className="arc-table">
                <thead><tr><th>Extension</th><th>Budget</th><th className="arc-td-right">Spent</th><th>Used</th><th>Resets</th></tr></thead>
                <tbody>
                  {spend.data.extensions.map((e) => (
                    <tr key={e.extension_id} style={onOpenExtension ? { cursor: 'pointer' } : undefined}
                      onClick={onOpenExtension ? () => onOpenExtension(e.extension_id) : undefined}>
                      <td><span style={{ fontWeight: 550 }}>{e.name}</span> <span className="arc-subtle arc-mono">{e.number}</span>
                        {e.over && <> <Badge tone="danger">calls stopped</Badge></>}</td>
                      <td className="arc-num">{formatMoney(e.budget, e.currency)} <span className="arc-subtle">{e.period}</span></td>
                      <td className="arc-td-right arc-num">{formatMoney(e.spent, e.currency)}</td>
                      <td><Bar spent={e.spent} limit={e.budget} /></td>
                      <td className="arc-subtle">{day(e.resets_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
}
